import { Component, Inject, Injector, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, AttendanceStatusEnum, Timeline, TimelineActionEnum, TimelineService, TimelineTypeEnum } from 'lib-trend-core';
import { finalize, takeUntil } from 'rxjs';

/**
 * Timeline entry with its labels already resolved for rendering.
 *
 * Newer actions carry structured `metadata` and are rendered from the i18n
 * catalogs; every legacy action falls back to the pt-BR `name`/`description`
 * persisted with the record, so historical entries render exactly as before.
 */
interface TimelineItemView extends Timeline {
  displayTitle: string;
  displayDescription: string;
  displayStatus: string;
  displayIcon: string;
}

@Component({
    selector: 'app-timeline-attendance',
    templateUrl: './timeline-attendance.component.html',
    styleUrls: ['./timeline-attendance.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TimelineAttendanceComponent extends AbstractComponent {
  public timelineData = signal<Array<TimelineItemView>>([]);

  private static readonly ACTION_ICONS: Partial<Record<TimelineActionEnum, string>> = {
    [TimelineActionEnum.TAG_ADDED]: 'mdi mdi-tag-plus',
    [TimelineActionEnum.TAG_REMOVED]: 'mdi mdi-tag-minus',
    [TimelineActionEnum.CAMPAIGN_ORIGIN]: 'mdi mdi-bullhorn',
  };
  private static readonly DEFAULT_ICON = 'mdi mdi-adjust';

  constructor(
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data: { idAttendance: string },
    public dialogRef: MatDialogRef<TimelineAttendanceComponent>,
    private timelineService: TimelineService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getTimeline();
  }

  private getTimeline() {
    this.loading = true;
    this.timelineService.getTimeline(this.data.idAttendance, TimelineTypeEnum.ATTENDANCE)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (response: Array<Timeline>) => this.timelineData.set((response ?? []).map(item => this.toView(item))),
        error: (err) => console.error(err),
      });
  }

  /** Resolved once on data arrival — not per change-detection cycle, and never during ngOnInit. */
  private toView(item: Timeline): TimelineItemView {
    const status = item?.entityObj?.['status'] as AttendanceStatusEnum;
    const base = {
      ...item,
      displayIcon: TimelineAttendanceComponent.ACTION_ICONS[item?.action] ?? TimelineAttendanceComponent.DEFAULT_ICON,
      displayStatus: !!status ? this.getTranslatedStatusAttendance(status) : this.t('attendance.status.unknown'),
    };

    const tag = item?.metadata?.tagTitle;
    if (!!tag && item?.action === TimelineActionEnum.TAG_ADDED) {
      return {
        ...base,
        displayTitle: this.t('attendance.timeline.tagAdded'),
        displayDescription: this.t('attendance.timeline.tagAddedDescription', { tag }),
      };
    }
    if (!!tag && item?.action === TimelineActionEnum.TAG_REMOVED) {
      return {
        ...base,
        displayTitle: this.t('attendance.timeline.tagRemoved'),
        displayDescription: this.t('attendance.timeline.tagRemovedDescription', { tag }),
      };
    }

    if (item?.action === TimelineActionEnum.CAMPAIGN_ORIGIN) {
      const campaign = item?.metadata?.campaignName;
      return {
        ...base,
        displayTitle: this.t('attendance.timeline.campaignOrigin'),
        displayDescription: !!campaign
          ? this.t('attendance.timeline.campaignOriginDescription', { campaign })
          : this.t('attendance.timeline.campaignOriginDescriptionUnknown'),
      };
    }

    return { ...base, displayTitle: item?.name, displayDescription: item?.description };
  }

  close() {
    this.dialogRef.close();
  }
}
